import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import { CloseSvg } from "../../../../../simulate/scenario/svgs/Svgs";
import "./ContactManagementModal.scss";
import { GroundContactSvg } from "../Svgs";
import { showToastMessage } from "../../../../../../utils/commonFunctions";
import useContacts from "../../../../hooks/useContacts";
import GroundStationService from "../../../../../../service/GroundStationService";
import { useParams } from "react-router-dom";
import ConfirmationDialog from "../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import CustomSelect from "../../../../../CommonComponents/CustomSelect";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import CustomTimePicker from "../../../../../CommonComponents/CustomTimePicker";
import useUrlParams from "../../../../hooks/useUrlParams";

dayjs.extend(utc);

// ------------------------------
// Type Definitions
// ------------------------------
interface BandInfo {
    bandName: string;
    uplinkBandId?: string;
    downlinkBandId?: string;
}
interface DropdownOption {
    label: string;
    value: string;
    providerId?: string;
    groundStations?: string[];
    frequency?: string[];
    bandDetails?: BandInfo[];
}

type ModalMode = "create" | "book" | null;

// ------------------------------
// Custom Hook: Draggable Modal
// ------------------------------
const useDraggableModal = () => {
    const modalRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isVisible, setIsVisible] = useState(false);

    // Calculate initial modal position when visible
    const calculateInitialPosition = useCallback(() => {
        if (!modalRef.current) return;
        const { width, height } = modalRef.current.getBoundingClientRect();
        const rightOffset = window.innerWidth * 0.25 + 36;
        const bottomOffset = 148;
        setPosition({
            x: window.innerWidth - rightOffset - width,
            y: window.innerHeight - bottomOffset - height,
        });
    }, []);

    useEffect(() => {
        if (isVisible) {
            // Delay to allow CSS transition
            setTimeout(calculateInitialPosition, 10);
        }
    }, [isVisible, calculateInitialPosition]);

    // Start dragging
    const startDrag = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            if (!modalRef.current) return;
            setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
            setIsDragging(true);
            modalRef.current.style.transition = "none";
        },
        [position],
    );

    // During drag
    const onDrag = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !modalRef.current) return;
            const { width, height } = modalRef.current.getBoundingClientRect();
            const maxX = window.innerWidth - width;
            const maxY = window.innerHeight - height;
            let newX = e.clientX - dragOffset.x;
            let newY = e.clientY - dragOffset.y;
            newX = Math.max(0, Math.min(maxX, newX));
            newY = Math.max(0, Math.min(maxY, newY));
            setPosition({ x: newX, y: newY });
        },
        [isDragging, dragOffset],
    );

    // End dragging
    const endDrag = useCallback(() => {
        setIsDragging(false);
        if (modalRef.current)
            modalRef.current.style.transition =
                "transform 0.2s ease, opacity 0.2s ease";
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", onDrag);
            window.addEventListener("mouseup", endDrag);
        }
        return () => {
            window.removeEventListener("mousemove", onDrag);
            window.removeEventListener("mouseup", endDrag);
        };
    }, [isDragging, onDrag, endDrag]);

    return { modalRef, position, startDrag, isVisible, setIsVisible };
};

// ------------------------------
// Helper: Toggle Minimize/Maximize
// ------------------------------
const toggleMinimize = (
    event: React.MouseEvent,
    setIsMinimised: (b: boolean) => void,
) => {
    const body = document.getElementsByClassName(
        "create__contact__body",
    )[0] as HTMLElement;
    const footer = document.getElementsByClassName(
        "create__contact__footer",
    )[0] as HTMLElement;
    const icon = (event.currentTarget as HTMLElement).querySelector("i");
    if (body && footer && icon) {
        const hidden = body.style.display === "none";
        body.style.display = hidden ? "flex" : "none";
        footer.style.display = hidden ? "flex" : "none";
        icon.classList.replace(
            hidden ? "fe-plus" : "fe-minus",
            hidden ? "fe-minus" : "fe-plus",
        );
        setIsMinimised(!hidden);
    }
};

// ------------------------------
// Main Component
// ------------------------------
const ContactManagementModal: React.FC<{
    isOpen: ModalMode;
    onClose: () => void;
    isMinimised: boolean;
    setIsMinimised: (b: boolean) => void;
}> = ({ isOpen, onClose, isMinimised, setIsMinimised }) => {
    const { modalRef, position, startDrag, isVisible, setIsVisible } =
        useDraggableModal();
    const { post_contact, selectedContact, book_contact } = useContacts();
    const { mission_id } = useParams<{ mission_id: string }>();

    const [gsProviders, setGsProviders] = useState<DropdownOption[]>([]);
    const [allGroundStations, setAllGroundStations] = useState<any[]>([]);
    const [selectedProvider, setSelectedProvider] =
        useState<DropdownOption | null>(null);
    const [selectedGroundStation, setSelectedGroundStation] =
        useState<DropdownOption | null>(null);
    const [selectedFrequencyBands, setSelectedFrequencyBands] = useState<
        string[] | null
    >(null);
    const [uplinkId, setUplinkId] = useState("");
    const [downlinkId, setDownlinkId] = useState("");
    const [startTimeMs, setStartTimeMs] = useState<number | null>(null);
    const [endTimeMs, setEndTimeMs] = useState<number | null>(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const { sat_id } = useUrlParams()


    // ------------------------------
    // Fetch Providers & Stations
    // ------------------------------
    useEffect(() => {
        if (!isOpen) return;
        console.log("sat_id", sat_id);

        let cancelled = false;

        (async () => {
            try {
                // fetch both in parallel
                const [provResult, gsResult] = await Promise.allSettled([
                    GroundStationService.getAllGsProviders(mission_id),
                    GroundStationService.getGroundStationsByMissionId(),
                ]);

                if (cancelled) return;

                // set ground stations
                if (gsResult.status === "fulfilled") {
                    setAllGroundStations(gsResult.value?.data ?? []);
                }

                // providers safe fallback
                const providersRaw: any[] =
                    provResult.status === "fulfilled" ? provResult.value?.data ?? [] : [];

                const provs: DropdownOption[] = [];
                // one pass over providers
                for (let i = 0; i < providersRaw.length; i++) {
                    const p = providersRaw[i];
                    if (!p) continue;

                    const groups = p.MQTTTopicList ?? [];
                    if (!groups.length) continue;

                    const bandDetails: BandInfo[] = [];

                    // iterate groups
                    for (let j = 0; j < groups.length; j++) {
                        const grp = groups[j];
                        if (!grp) continue;

                        // match check only once
                        const matches =
                            (grp.satelliteId && grp.satelliteId === sat_id) ||
                            (grp.gspSatId && grp.gspSatId === sat_id) ||
                            (grp.satelliteName && grp.satelliteName === sat_id);

                        if (!matches) continue;

                        const topics = grp.topic ?? [];
                        if (!topics.length) continue;

                        // iterate topics
                        for (let k = 0; k < topics.length; k++) {
                            const t = topics[k];
                            if (!t) continue;

                            bandDetails.push({
                                bandName: t.bandName ?? t.name ?? "Unknown",
                                uplinkBandId: t.bandId?.uplink ?? t.acpBandUid?.uplink,
                                downlinkBandId: t.bandId?.downlink ?? t.acpBandUid?.downlink,
                            });
                        }
                    }

                    // push provider only if relevant
                    if (bandDetails.length) {
                        provs.push({
                            label: p.providerName ?? p.name ?? "Unnamed Provider",
                            value: p.providerName ?? p.name ?? p.uniqueId ?? "",
                            providerId: p.uniqueId ?? p.providerId ?? p.id ?? "",
                            groundStations: p.groundStations ?? [],
                            frequency:
                                p.mqttConfiguredBands ??
                                bandDetails.map((b) => b.bandName), // fallback
                            bandDetails,
                        });
                    }
                }

                if (!cancelled) {
                    setGsProviders(provs);
                }
            } catch (err) {
                console.error("fetch providers/stations failed:", err);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, mission_id, sat_id]);


    // ------------------------------
    // Initialize Form
    // ------------------------------
    useEffect(() => {
        if (isOpen === "create") {
            setSelectedProvider(null);
            setSelectedGroundStation(null);
            setSelectedFrequencyBands(null);
            setUplinkId("");
            setDownlinkId("");
            const now = dayjs.utc();
            setStartTimeMs(now.add(2, "minute").valueOf());
            setEndTimeMs(now.add(7, "minute").valueOf());
        } else if (isOpen === "book" && selectedContact) {
            const d = selectedContact.contact_data;
            setSelectedProvider(
                gsProviders.find((p) => p.providerId === d.gspId) || null,
            );
            setSelectedGroundStation({
                label: d.groundStationName,
                value: d.groundStationId || d.groundStationName,
            });
            const freq =
                d.groundStationProperties?.freqbands?.[0] || d.bandInfo?.bandName;
            setSelectedFrequencyBands(freq ? [freq] : null);
            setUplinkId(d.uplink?.bandId || "");
            setDownlinkId(d.downlink?.bandId || "");
            setStartTimeMs(dayjs.unix(d.startTime).utc().valueOf());
            setEndTimeMs(dayjs.unix(d.endTime).utc().valueOf());
        }
    }, [isOpen, gsProviders]);

    // ------------------------------
    // Visibility
    // ------------------------------
    useEffect(() => {
        setIsVisible(!!isOpen);
    }, [isOpen, setIsVisible]);

    // ------------------------------
    // Validate Form
    // ------------------------------
    useEffect(() => {
        if (isOpen === "book") {
            setIsFormValid(true);
            return;
        }
        const valid =
            !!selectedProvider &&
            !!selectedGroundStation &&
            selectedFrequencyBands?.length === 1 &&
            !!uplinkId &&
            !!downlinkId
        setIsFormValid(Boolean(valid));
    }, [
        isOpen,
        selectedProvider,
        selectedGroundStation,
        selectedFrequencyBands,
        uplinkId,
        downlinkId
    ]);

    // ------------------------------
    // Auto-select: Provider (if only one option)
    // ------------------------------
    useEffect(() => {
        if (isOpen !== "create") return;
        if (gsProviders.length === 1 && !selectedProvider) {
            handleProviderChange(gsProviders[0]);
        }
    }, [gsProviders, isOpen]);

    // ------------------------------
    // Auto-select: Ground Station (if only one option after provider selected)
    // ------------------------------
    useEffect(() => {
        if (isOpen !== "create" || !selectedProvider) return;
        const options = getGroundStationOptions();
        if (options.length === 1 && !selectedGroundStation) {
            setSelectedGroundStation(options[0]);
        }
    }, [selectedProvider, allGroundStations, isOpen]);

    // ------------------------------
    // Auto-select: Frequency (if only one option after provider selected)
    // ------------------------------
    useEffect(() => {
        if (isOpen !== "create" || !selectedProvider) return;
        const freqs = selectedProvider.frequency || [];
        if (freqs.length === 1 && !selectedFrequencyBands) {
            const freq = freqs[0];
            const bandInfo = selectedProvider.bandDetails?.find((b) => b.bandName === freq);
            setSelectedFrequencyBands([freq]);
            if (bandInfo) {
                setUplinkId(bandInfo.uplinkBandId || "");
                setDownlinkId(bandInfo.downlinkBandId || "");
            }
        }
    }, [selectedProvider, isOpen]);

    // ------------------------------
    // Handlers: Time Pickers
    // ------------------------------
    const handleStartTimeChange = (ms: number | null) => {
        if (isOpen === "book") return;
        setStartTimeMs(ms);
    };
    const handleEndTimeChange = (ms: number | null) => {
        if (isOpen === "book") return;
        setEndTimeMs(ms);
    };

    // ------------------------------
    // Handlers: Dropdowns & Inputs
    // ------------------------------
    const handleProviderChange = (o: DropdownOption | null) => {
        setSelectedProvider(o);
        setSelectedGroundStation(null);
        // Auto select first frequency if nothing is selected
        const firstFreq = o?.frequency?.[0];

        // Reset if no frequency available
        if (!firstFreq) {
            setSelectedFrequencyBands(null);
            setUplinkId("");
            setDownlinkId("");
            return;
        }

        const info = o.bandDetails?.find(
            (b: BandInfo) => b.bandName === firstFreq,
        );

        setSelectedFrequencyBands([firstFreq]);

        if (isOpen === "create" && info) {
            setUplinkId(info.uplinkBandId || "");
            setDownlinkId(info.downlinkBandId || "");
        } else {
            setUplinkId("");
            setDownlinkId("");
        }
    };
    const handleGroundStationChange = (o: DropdownOption | null) => 
        setSelectedGroundStation(o);
    const getGroundStationOptions = useCallback(
        () =>
            selectedProvider
                ? allGroundStations
                    .filter((gs) =>
                        selectedProvider.groundStations?.includes(gs.groundStationId),
                    )
                    .map((gs) => ({
                        label: gs.groundStationName,
                        value: gs.groundStationId,
                    }))
                : [],
        [selectedProvider, allGroundStations],
    );
    const handleFrequencyAdd = (freq: string, bandInfo?: BandInfo) => {
        // Prevent deselecting the currently selected band
        if (selectedFrequencyBands?.[0] === freq) {
            return;
        }

        setSelectedFrequencyBands([freq]);

        if (isOpen === "create" && bandInfo) {
            setUplinkId(bandInfo.uplinkBandId || "");
            setDownlinkId(bandInfo.downlinkBandId || "");
        }
    };
    const handleUplinkChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        setUplinkId(e.target.value);
    const handleDownlinkChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        setDownlinkId(e.target.value);

    // ------------------------------
    // Handlers: Submit & Booking
    // ------------------------------
    const handleContact = () => {
        if (isOpen === "book" && selectedContact) {
            setShowConfirmationDialog(true);
            return;
        }
        if (!isFormValid) {
            showToastMessage("error", "Please fill all required fields correctly");
            return;
        }
        // Time validation
        if (startTimeMs && endTimeMs) {
            const st = dayjs.utc(startTimeMs);
            const et = dayjs.utc(endTimeMs);

            if (!st.isValid() || !et.isValid()) {
                showToastMessage("error", "Invalid start or end time");
                return;
            }

            if (!et.isAfter(st)) {
                showToastMessage("error", "End time must be after start time");
                return;
            }

            const diff = et.diff(st, "minute");
            if (diff < 1 || diff > 180) {
                showToastMessage("error", "Contact duration must be between 1 and 180 minutes");
                return;
            }
        } else {
            if (!startTimeMs) {
                showToastMessage("error", "Start time is required");
                return;
            }

            if (!endTimeMs) {
                showToastMessage("error", "End time is required");
                return;
            }
        }
        const payload = {
            providerId: selectedProvider!.providerId,
            groundStationId: selectedGroundStation!.value,
            freqBands: selectedFrequencyBands || [],
            uplinkId,
            downlinkId,
            startTime: dayjs.utc(startTimeMs!).unix(),
            endTime: dayjs.utc(endTimeMs!).unix(),
        };
        post_contact(payload);
        onClose();
    };
    const handleBookingConfirmation = () => {
        if (!selectedContact) return;
        book_contact(selectedContact.contact_data.uniqueId);
        setShowConfirmationDialog(false);
        onClose();
    };

    // ------------------------------
    // Render: Null if Closed
    // ------------------------------
    if (!isOpen) return null;

    // ------------------------------
    // Render: JSX
    // ------------------------------
    return (
        <>
            <ConfirmationDialog
                type="book"
                open={showConfirmationDialog}
                title="Book Available Contact?"
                description="Confirm to book contact."
                onCancel={() => setShowConfirmationDialog(false)}
                onConfirm={handleBookingConfirmation}
                confirm_button_text="Confirm"
            />
            <div
                id="create__contact__modal"
                className={`create__contact__container ${isVisible ? "visible" : ""}`}
                ref={modalRef}
                style={{
                    top: `${position.y}px`,
                    left: `${position.x}px`,
                }}
            >
                {/* HEADER */}
                <div
                    className="create__contact__header"
                    onMouseDown={startDrag}
                >
                    <div className="d-flex gap-2 align-items-center">
                        <GroundContactSvg />
                        <span className="header__title">
                            {isOpen === "book" ? "BOOKING" : "CREATE"} CONTACT
                        </span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <CustomToolTip
                            title={isMinimised ? "Maximize" : "Minimize"}
                            placement="top"
                        >
                            <button
                                className="header__button"
                                onClick={(e) => toggleMinimize(e, setIsMinimised)}
                            >
                                <i className="fe fe-minus" />
                            </button>
                        </CustomToolTip>
                        <CustomToolTip title="Close" placement="top">
                            <button className="header__button" onClick={onClose}>
                                <CloseSvg />
                            </button>
                        </CustomToolTip>
                    </div>
                </div>
                {/* BODY */}
                <div className="create__contact__body">
                    {/* Section1: Provider & Station */}
                    <div className="create__contact__body__section1">
                        <div className="item">
                            <span className="title">Provider</span>
                            {isOpen === "book" ? (
                                <input
                                    type="text"
                                    className="contact-input-container"
                                    value={selectedProvider?.label || ""}
                                    readOnly
                                />
                            ) : (
                                <CustomSelect
                                    id="Provider"
                                    value={selectedProvider}
                                    onChange={handleProviderChange}
                                    options={gsProviders}
                                    placeholder="Select Provider"
                                    controllHeight="24px"
                                />
                            )}
                        </div>
                        <div className="item">
                            <span className="title">Ground Station</span>
                            {isOpen === "book" ? (
                                <input
                                    type="text"
                                    className="contact-input-container"
                                    value={selectedGroundStation?.label || ""}
                                    readOnly
                                />
                            ) : (
                                <CustomSelect
                                    id="Station"
                                    value={selectedGroundStation}
                                    onChange={handleGroundStationChange}
                                    options={getGroundStationOptions()}
                                    placeholder="Select Ground Station"
                                    controllHeight="24px"
                                />
                            )}
                        </div>
                    </div>
                    {/* Section2: Transceiver Details */}
                    <div className="create__contact__body__section2">
                        <div className="header__section2">
                            SATELLITE TRANSCEIVER DETAILS
                        </div>
                        <div className="item">
                            <span className="title">Frequency</span>
                            <div className="frequency-div">
                                {(isOpen === "book"
                                    ? selectedFrequencyBands || []
                                    : selectedProvider?.frequency || []
                                ).map((freq) => {
                                    const info = selectedProvider?.bandDetails?.find(
                                        (b) => b.bandName === freq,
                                    );
                                    return (
                                        <button
                                            key={freq}
                                            onClick={() => handleFrequencyAdd(freq, info)}
                                            className={
                                                selectedFrequencyBands?.[0] === freq ? "active" : ""
                                            }
                                        >
                                            {freq.replace("-band", "")}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="item">
                            <span className="title">Uplink ID</span>
                            <input
                                type="text"
                                value={uplinkId}
                                onChange={handleUplinkChange}
                                placeholder="Enter uplink ID"
                                autoComplete="off"
                                readOnly={isOpen === "book"}
                            />
                        </div>
                        <div className="item">
                            <span className="title">Downlink ID</span>
                            <input
                                type="text"
                                value={downlinkId}
                                onChange={handleDownlinkChange}
                                placeholder="Enter downlink ID"
                                autoComplete="off"
                                readOnly={isOpen === "book"}
                            />
                        </div>
                    </div>
                    {/* Section3: Contact Time */}
                    <div className="create__contact__body__section3">
                        <div className="header__section3">CONTACT TIME</div>
                        <div className="item">
                            <span className="title">Start Time</span>
                            <div className="d-flex align-items-center border rounded-1 overflow-hidden p-0 w-100">
                                <CustomTimePicker
                                    value={startTimeMs}
                                    onChange={(val) =>
                                        handleStartTimeChange(val ? val.valueOf() : null)
                                    }
                                    disabled={isOpen === "book"}
                                />
                            </div>
                        </div>
                        <div className="item">
                            <span className="title">End Time</span>
                            <div className="d-flex align-items-center border rounded-1 overflow-hidden p-0 w-100">
                                <CustomTimePicker
                                    value={endTimeMs}
                                    onChange={(val) =>
                                        handleEndTimeChange(val ? val.valueOf() : null)
                                    }
                                    disabled={isOpen === "book"}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* FOOTER */}
                <div className="create__contact__footer">
                    <button className="contact__footer__close__button" onClick={onClose}>
                        Close
                    </button>
                    <button
                        className="contact__footer__next__button"
                        disabled={!isFormValid}
                        onClick={handleContact}
                    >
                        {isOpen === "create" ? "Create" : "Book"}
                    </button>
                </div>
            </div>
        </>
    );
};

export default ContactManagementModal;
